import React, { useState, useEffect } from "react";
import { useHistory, useParams, Link } from "react-router-dom";
import useSWR, { mutate } from "swr";
import request, { get } from "axios";
import { useClient } from "../api";
import isSuperUser from "../../../helpers/bookingsSuperUsers";

const BookingDetails = ({ token, currentUserEmail }) => {
  const client = useClient();
  const history = useHistory();
  const [approvalLoading, setApprovalLoading] = useState(false);
  const { vacancyID } = useParams();
  const { data: vacancy, error } = useSWR(`/vacancies/${vacancyID}`);

  const [workers, setWorkers] = useState([]);
  const [assignedWorkers, setAssignedWorkers] = useState([]);
  // const [workersRef, setWorkersRefs] = useState([]);
  if (vacancy && vacancy.assignedWorkers) {
    const workersList = vacancy.assignedWorkers.map(el => el.candidateRef);
    if (workersList.length !== workers.length) {
      setWorkers(workersList);
    }
  }

  useEffect(() => {
    if (workers) {
      Promise.all(
        workers.map(async candidateRef =>
          get(
            `${process.env.REACT_APP_PROFILE_API_URL}/clientDashboard/worker/${candidateRef}`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          )
        )
      ).then(res => {
        const mapped = res.map((data, i) => ({
          ...data.data,
          ref: workers[i]
        }));

        setAssignedWorkers(mapped);
      });
    }
  }, [workers]);

  if (error) {
    return "Something's gone wrong";
  }

  if (!vacancy) {
    return <></>;
  }

  const submitApproval = async () => {
    setApprovalLoading(true);
    try {
      await client.patch(`/vacancies/${vacancyID}`, { state: "APPROVED" });

      const emailAddress = vacancy.creatorEmail;

      const emailValues = {
        vacancyTitle: vacancy.title,
        vacancyLocation: vacancy.location
      };
      if (emailAddress) {
        await request.post(
          // send email once approver approves vacancy
          `${process.env.REACT_APP_PROFILE_API_URL}/clientDashboard/sendVacancyApprovedEmail`,
          {
            emailAddress,
            details: emailValues
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      // Use mutate to update SWR cache so page change is instant
      mutate(`/vacancies${vacancyID}`, { ...vacancy, state: "APPROVED" });
      history.push(`/vacancies`);
    } catch (err) {
      console.error(err);
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleDownloadCV = candidateData => {
    window.open(
      `${process.env.REACT_APP_PROFILE_API_URL}/clientDashboard/worker/${candidateData.ref}/cv/${candidateData.cv}?token=${token}`,
      "_blank"
    );
  };

  const DetailsRow = ({ children }) => (
    <div className="tw-mt-2 tw-font-normal tw-text-sm tw-text-gray-500 tw-flex md:tw-px-10  sm:tw-px-8  tw-justify-between tw-p-2 tw-bg-white">
      {children}
    </div>
  );

  return (
    <div className="lg:tw-px-32 tw-pb-8">
      <div className="tw-p-4 tw-rounded-md tw-bg-white tw-flex tw-flex-col tw-rounded tw-overflow-hidden tw-shadow-lg">
        <h1 className="tw-p-2 tw-ml-2 tw-text-lg tw-font-bold tw-font-normal tw-text-gray-500">
          Vacancy Details
        </h1>
      </div>
      <DetailsRow>
        <div>
          <p className="tw-font-bold">Title:</p>
          <div className="tw-pl-4 tw-pt-2">{vacancy.title}</div>
        </div>
      </DetailsRow>
      <DetailsRow>
        <div>
          <p className="tw-font-bold">Description:</p>
          <div className="tw-pl-4 tw-pt-2 tw-whitespace-pre-line">
            {vacancy.description}
          </div>
        </div>
      </DetailsRow>
      <DetailsRow>
        <div>
          <p className="tw-font-bold ">Location:</p>
          <div className="tw-pl-4 tw-pt-2">
            {vacancy.location &&
            !vacancy.address &&
            !vacancy.town &&
            !vacancy.county &&
            !vacancy.postcode
              ? vacancy.location
              : `${vacancy.address}, ${vacancy.town}, ${vacancy.county}, ${vacancy.postcode}`}
          </div>
        </div>
      </DetailsRow>
      <DetailsRow>
        <div>
          <p className="tw-font-bold ">Work Type:</p>
          <div className="tw-pl-4 tw-pt-2">{vacancy.workType}</div>
        </div>
      </DetailsRow>
      <DetailsRow>
        <div>
          <p className="tw-font-bold ">Reason for New Vacancy:</p>
          <div className="tw-pl-4 tw-pt-2">{vacancy.reason}</div>
        </div>
      </DetailsRow>
      <DetailsRow>
        <div>
          <p className="tw-font-bold ">Additional Information:</p>
          <div className="tw-pl-4 tw-pt-2 tw-whitespace-pre-line">
            {vacancy.additionalInformation}
          </div>
        </div>
      </DetailsRow>
      <DetailsRow>
        <div>
          <p className="tw-font-bold ">Skills Required:</p>
          {vacancy.qualificationRequirements.length !== 0 ? (
            vacancy.qualificationRequirements.map((req, i) => (
              <div key={i} className="tw-pl-4 tw-pt-2">
                - {req}
              </div>
            ))
          ) : (
            <div className="tw-pl-4 tw-pt-2">Not specified</div>
          )}
        </div>
      </DetailsRow>
      <DetailsRow>
        <div>
          <p className="tw-font-bold ">Assigned Workers:</p>
          <div className="tw-pl-4 tw-pt-2">
            {assignedWorkers.length > 0 ? (
              Object.keys(assignedWorkers).map((el, i) => (
                <li className="tw-flex tw-py-2" key={i}>
                  <p className="tw-mr-2">
                    {`${i + 1}. `}
                    {assignedWorkers[el].name}
                  </p>
                  <p
                    className="tw-cursor-pointer"
                    onClick={() => handleDownloadCV(assignedWorkers[el])}
                  >
                    {assignedWorkers[el].cv && `(Download CV)`}
                  </p>
                </li>
              ))
            ) : (
              <p className="tw-flex">No workers assigned</p>
            )}
          </div>
        </div>
      </DetailsRow>
      <div className="tw-items-center tw-bg-gray-100 tw-justify-start tw-mt-4">
        <div className="tw-p-4 ">
          {vacancy.state === "PENDING" &&
            (vacancy.approverEmail === currentUserEmail ||
              isSuperUser(currentUserEmail)) && (
              <button
                onClick={submitApproval}
                disabled={approvalLoading ? "disabled" : ""}
                className="tw-bg-green-600 tw-py-2 tw-px-4 tw-text-sm tw-rounded tw-inline-flex tw-items-center tw-mr-2"
              >
                {approvalLoading ? "Approving..." : "Approve"}
              </button>
            )}

          <Link
            className="tw-bg-gray-300 tw-py-2 tw-px-4 tw-text-sm tw-rounded tw-inline-flex tw-items-center"
            to={"/vacancies"}
          >
            Back to overview
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BookingDetails;
